import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Edit, Trash2, Search, User, CreditCard } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [subscriptions, setSubscriptions] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchFilter, setSearchFilter] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [changingPlan, setChangingPlan] = useState(null);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({ open: false, userId: null, userEmail: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, plansRes] = await Promise.all([
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/subscriptions/plans`)
      ]);
      
      setUsers(usersRes.data || []);
      setPlans(plansRes.data || []);
      
      // Fetch subscriptions for all users
      const subsPromises = (usersRes.data || []).map(user => 
        axios.get(`${API}/admin/users/${user.id}/subscription`).catch(() => null)
      );
      const subsResults = await Promise.all(subsPromises);
      
      const subsMap = {};
      usersRes.data.forEach((user, idx) => {
        if (subsResults[idx]?.data) {
          subsMap[user.id] = subsResults[idx].data;
        }
      });
      setSubscriptions(subsMap);
    } catch (error) {
      toast.error('Fehler beim Laden der Daten');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = async () => {
    try {
      await axios.put(`${API}/admin/users/${editingUser.id}`, {
        name: editingUser.name,
        email: editingUser.email,
        company: editingUser.company,
        phone: editingUser.phone,
        is_admin: editingUser.is_admin
      });
      toast.success('Benutzer aktualisiert');
      setEditingUser(null);
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
      console.error(error);
    }
  };

  const handleDeleteUser = async () => {
    const userId = deleteConfirmDialog.userId;
    if (!userId) return;
    
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success('Benutzer gelöscht');
      setDeleteConfirmDialog({ open: false, userId: null, userEmail: '' });
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Löschen');
      console.error('Delete error:', error);
    }
  };
  
  const openDeleteConfirm = (userId, userEmail) => {
    setDeleteConfirmDialog({ 
      open: true, 
      userId, 
      userEmail 
    });
  };

  const handleChangePlan = async () => {
    try {
      await axios.put(`${API}/admin/users/${changingPlan.userId}/subscription`, {
        plan_id: changingPlan.newPlanId
      });
      toast.success('Abo-Plan geändert');
      setChangingPlan(null);
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Ändern des Plans');
      console.error(error);
    }
  };

  const filteredUsers = users.filter(user => {
    if (!searchFilter) return true;
    const search = searchFilter.toLowerCase();
    return (
      user.email?.toLowerCase().includes(search) ||
      user.name?.toLowerCase().includes(search) ||
      user.company?.toLowerCase().includes(search)
    );
  });

  const getPlanName = (userId) => {
    const sub = subscriptions[userId];
    if (!sub) return 'Kein Abo';
    const plan = plans.find(p => p.id === sub.plan_id);
    return plan?.name || 'Unbekannt';
  };

  return (
    <div className="min-h-screen p-8" style={{ backgroundColor: '#0B0F14' }}>
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-3xl font-bold" style={{ color: '#E6EDF3', fontFamily: 'Manrope' }}>
            User Management
          </h2>
        </div>

        {/* Search */}
        <div className="mb-6 relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{ color: '#94A3B8' }} />
          <Input
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            placeholder="Suche nach E-Mail, Name oder Firma..."
            className="pl-10"
            style={{ backgroundColor: '#0F141A', borderColor: '#1F2937', color: '#E6EDF3' }}
          />
        </div>

        <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
          <CardHeader>
            <CardTitle style={{ color: '#E6EDF3' }}>
              Alle Benutzer ({filteredUsers.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p style={{ color: '#94A3B8' }}>Laden...</p>
            ) : filteredUsers.length === 0 ? (
              <p style={{ color: '#94A3B8' }}>
                {searchFilter ? 'Keine Benutzer gefunden' : 'Noch keine Benutzer'}
              </p>
            ) : (
              <div className="space-y-3">
                {filteredUsers.map((user) => (
                  <div
                    key={user.id}
                    className="p-4 rounded-lg"
                    style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', border: '1px solid' }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <div 
                            className="w-10 h-10 rounded-full flex items-center justify-center"
                            style={{ backgroundColor: '#1EA0E620' }}
                          >
                            <User className="w-5 h-5" style={{ color: '#7CD4FD' }} />
                          </div>
                          <div>
                            <p className="font-semibold" style={{ color: '#E6EDF3' }}>
                              {user.name || user.email}
                            </p>
                            <p className="text-sm" style={{ color: '#94A3B8' }}>{user.email}</p>
                          </div>
                          {user.is_admin && (
                            <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: '#1EA0E6', color: '#fff' }}>
                              ADMIN
                            </span>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-2 gap-2 text-sm mt-3">
                          {user.company && (
                            <div>
                              <span style={{ color: '#94A3B8' }}>Firma: </span>
                              <span style={{ color: '#E6EDF3' }}>{user.company}</span>
                            </div>
                          )}
                          {user.phone && (
                            <div>
                              <span style={{ color: '#94A3B8' }}>Telefon: </span>
                              <span style={{ color: '#E6EDF3' }}>{user.phone}</span>
                            </div>
                          )}
                          <div>
                            <span style={{ color: '#94A3B8' }}>Abo: </span>
                            <span style={{ color: '#7CD4FD' }}>{getPlanName(user.id)}</span>
                          </div>
                          <div>
                            <span style={{ color: '#94A3B8' }}>Erstellt: </span>
                            <span style={{ color: '#E6EDF3' }}>
                              {new Date(user.created_at).toLocaleDateString('de-DE')}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => setChangingPlan({ userId: user.id, currentPlan: getPlanName(user.id), newPlanId: null })}
                          variant="outline"
                          style={{ borderColor: '#1F2937', color: '#E6EDF3' }}
                        >
                          <CreditCard className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => setEditingUser(user)}
                          variant="outline"
                          style={{ borderColor: '#1F2937', color: '#E6EDF3' }}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => openDeleteConfirm(user.id, user.email)}
                          variant="outline"
                          style={{ borderColor: '#EF4444', color: '#EF4444' }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Edit User Dialog */}
        <Dialog open={editingUser !== null} onOpenChange={() => setEditingUser(null)}>
          <DialogContent style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
            <DialogHeader>
              <DialogTitle style={{ color: '#E6EDF3' }}>Benutzer bearbeiten</DialogTitle>
            </DialogHeader>
            {editingUser && (
              <div className="space-y-4 mt-4">
                <div>
                  <Label style={{ color: '#E6EDF3' }}>E-Mail</Label>
                  <Input
                    value={editingUser.email}
                    onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                    style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                  />
                </div>
                <div>
                  <Label style={{ color: '#E6EDF3' }}>Name</Label>
                  <Input
                    value={editingUser.name || ''}
                    onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                    style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                  />
                </div>
                <div>
                  <Label style={{ color: '#E6EDF3' }}>Firma</Label>
                  <Input
                    value={editingUser.company || ''}
                    onChange={(e) => setEditingUser({ ...editingUser, company: e.target.value })}
                    style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                  />
                </div>
                <div>
                  <Label style={{ color: '#E6EDF3' }}>Telefon</Label>
                  <Input
                    value={editingUser.phone || ''}
                    onChange={(e) => setEditingUser({ ...editingUser, phone: e.target.value })}
                    style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={editingUser.is_admin || false}
                    onChange={(e) => setEditingUser({ ...editingUser, is_admin: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <Label style={{ color: '#E6EDF3' }}>Admin-Rechte</Label>
                </div>
                <Button
                  onClick={handleEditUser}
                  className="w-full"
                  style={{ backgroundColor: '#1EA0E6', color: '#ffffff' }}
                >
                  Speichern
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Change Plan Dialog */}
        <Dialog open={changingPlan !== null} onOpenChange={() => setChangingPlan(null)}>
          <DialogContent style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
            <DialogHeader>
              <DialogTitle style={{ color: '#E6EDF3' }}>Abo-Plan ändern</DialogTitle>
            </DialogHeader>
            {changingPlan && (
              <div className="space-y-4 mt-4">
                <p style={{ color: '#94A3B8' }}>
                  Aktueller Plan: <span style={{ color: '#7CD4FD' }}>{changingPlan.currentPlan}</span>
                </p>
                <div>
                  <Label style={{ color: '#E6EDF3' }}>Neuer Plan</Label>
                  <select
                    value={changingPlan.newPlanId || ''}
                    onChange={(e) => setChangingPlan({ ...changingPlan, newPlanId: e.target.value })}
                    className="w-full p-2 rounded border mt-2"
                    style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                  >
                    <option value="">Bitte wählen...</option>
                    {plans.map(plan => (
                      <option key={plan.id} value={plan.id}>
                        {plan.name} - €{plan.price}/{plan.interval === 'month' ? 'Monat' : plan.interval === 'year' ? 'Jahr' : 'einmalig'}
                      </option>
                    ))}
                  </select>
                </div>
                <Button
                  onClick={handleChangePlan}
                  disabled={!changingPlan.newPlanId}
                  className="w-full"
                  style={{ backgroundColor: '#1EA0E6', color: '#ffffff' }}
                >
                  Plan ändern
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteConfirmDialog.open} onOpenChange={(open) => setDeleteConfirmDialog({ open, userId: null, userEmail: '' })}>
          <DialogContent style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
            <DialogHeader>
              <DialogTitle style={{ color: '#E6EDF3' }}>Benutzer löschen?</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p style={{ color: '#94A3B8' }}>
                Möchten Sie den Benutzer <strong style={{ color: '#E6EDF3' }}>{deleteConfirmDialog.userEmail}</strong> wirklich löschen?
              </p>
              <p style={{ color: '#EF4444', fontSize: '0.875rem' }}>
                ⚠️ Diese Aktion kann nicht rückgängig gemacht werden. Alle Daten des Benutzers (Fahrzeuge, Diagnosen, etc.) werden gelöscht.
              </p>
              <div className="flex gap-3 justify-end">
                <Button
                  variant="outline"
                  onClick={() => setDeleteConfirmDialog({ open: false, userId: null, userEmail: '' })}
                  style={{ borderColor: '#1F2937', color: '#E6EDF3' }}
                >
                  Abbrechen
                </Button>
                <Button
                  onClick={handleDeleteUser}
                  style={{ backgroundColor: '#EF4444', color: '#ffffff' }}
                >
                  Benutzer löschen
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};
